import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    'filterType',
    'filterOptions',
    'messageContent',
    'preview',
    'filterParams',
    'userCount',
    'userCountNumber'
  ]

  connect() {
    console.log('BulkMessageForm controller connected')
    this.updatePreview()
  }

  filterTypeChanged(event) {
    const filterType = event.target.value
    this.updateFilterOptions(filterType)
    this.updateUserCount(filterType)
  }

  updateFilterOptions(filterType) {
    const optionsHtml = this.getFilterOptionsHtml(filterType)
    this.filterOptionsTarget.innerHTML = optionsHtml
  }

  getFilterOptionsHtml(filterType) {
    switch(filterType) {
      case 'low_sessions':
        return `
          <div class="mb-3">
            <label class="form-label">セッション残数の閾値</label>
            <input type="number" name="session_threshold" class="form-control" value="2" min="1" max="10" data-action="change->bulk-message-form#updateUserCount">
            <small class="text-muted">この数以下のセッションを持つユーザーが対象</small>
          </div>
        `
      case 'upcoming_reservation':
        return `
          <div class="mb-3">
            <label class="form-label">今後何日以内の予約</label>
            <input type="number" name="days_ahead" class="form-control" value="7" min="1" max="30" data-action="change->bulk-message-form#updateUserCount">
            <small class="text-muted">指定日数以内に予約があるユーザーが対象</small>
          </div>
        `
      case 'no_recent_reservation':
        return `
          <div class="mb-3">
            <label class="form-label">何日以上予約なし</label>
            <input type="number" name="days_since" class="form-control" value="30" min="7" max="90" data-action="change->bulk-message-form#updateUserCount">
            <small class="text-muted">指定日数以上予約がないユーザーが対象</small>
          </div>
        `
      case 'specific_plan':
        return `
          <div class="mb-3">
            <label class="form-label">対象プラン</label>
            <select name="plan_id" class="form-select" data-action="change->bulk-message-form#updateUserCount">
              <option value="">-- プランを選択 --</option>
              ${this.getPlanOptions()}
            </select>
          </div>
        `
      default:
        return ''
    }
  }

  getPlanOptions() {
    // プランのオプションはサーバーから取得するか、data属性で渡す
    return `
      <option value="1">ベーシック(4)</option>
      <option value="2">スタンダード(8)</option>
      <option value="3">プレミアム(12)</option>
    `
  }

  updateUserCount(filterType) {
    const params = this.collectFilterParams()

    fetch('/admin/bulk_messages/preview', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
      },
      body: JSON.stringify({
        filter_type: this.filterTypeTarget.value,
        ...params
      })
    })
    .then(response => response.text())
    .then(html => {
      // HTMLから対象ユーザー数を抽出して表示
      const parser = new DOMParser()
      const doc = parser.parseFromString(html, 'text/html')
      const countElement = doc.querySelector('.alert-success strong')
      if (countElement) {
        this.userCountNumberTarget.textContent = countElement.textContent
        this.userCountTarget.classList.remove('alert-warning')
        this.userCountTarget.classList.add('alert-info')
      } else {
        this.userCountNumberTarget.textContent = '0'
        this.userCountTarget.classList.remove('alert-info')
        this.userCountTarget.classList.add('alert-warning')
      }
    })
  }

  collectFilterParams() {
    const params = {}
    const inputs = this.filterOptionsTarget.querySelectorAll('input, select')
    inputs.forEach(input => {
      if (input.value) {
        params[input.name] = input.value
      }
    })
    return params
  }

  useTemplate(event) {
    const template = event.currentTarget.dataset.template
    this.messageContentTarget.value = template
    this.updatePreview()
  }

  updatePreview() {
    const content = this.messageContentTarget.value
    if (!content) {
      this.previewTarget.innerHTML = `
        <div class="text-muted text-center py-3">
          <i class="bi bi-chat-dots fs-1"></i>
          <p class="mb-0">メッセージを入力するとプレビューが表示されます</p>
        </div>
      `
      return
    }

    // サンプル変数で置換
    const sampleVars = {
      '{{名前}}': '山田太郎',
      '{{姓}}': '山田',
      '{{名}}': '太郎',
      '{{残セッション}}': '4',
      '{{現在のプラン}}': 'ベーシック(4)',
      '{{次回予約}}': '8/15 18:00',
      '{{今月}}': new Date().getMonth() + 1 + '月',
      '{{来月}}': ((new Date().getMonth() + 2) % 12 || 12) + '月'
    }

    let previewContent = content
    Object.entries(sampleVars).forEach(([key, value]) => {
      previewContent = previewContent.replace(new RegExp(key, 'g'), value)
    })

    this.previewTarget.innerHTML = `
      <div class="border rounded p-3 bg-light">
        <small class="text-muted d-block mb-2">※ サンプルユーザーでのプレビュー</small>
        <div class="bg-white border rounded p-3">
          ${previewContent.replace(/\n/g, '<br>')}
        </div>
      </div>
    `
  }

  async testPreview() {
    const params = this.collectFilterParams()

    const modal = `
      <div class="modal fade" id="previewModal" tabindex="-1">
        <div class="modal-dialog modal-lg">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">対象ユーザー確認</h5>
              <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
            </div>
            <div class="modal-body">
              <div class="text-center py-3">
                <div class="spinner-border text-primary" role="status">
                  <span class="visually-hidden">Loading...</span>
                </div>
                <p class="mt-2">読み込み中...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    `

    // モーダルを追加して表示
    document.body.insertAdjacentHTML('beforeend', modal)
    const modalElement = document.getElementById('previewModal')

    // 動的にBootstrapをインポート
    const bootstrap = await import("bootstrap")
    const Modal = bootstrap.Modal

    // インスタンスを取得または作成
    let bsModal = Modal.getInstance(modalElement)
    if (!bsModal) {
      bsModal = new Modal(modalElement)
    }
    bsModal.show()

    // プレビューデータを取得
    fetch('/admin/bulk_messages/preview', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
      },
      body: JSON.stringify({
        filter_type: this.filterTypeTarget.value,
        message_content: this.messageContentTarget.value,
        ...params
      })
    })
    .then(response => response.text())
    .then(html => {
      modalElement.querySelector('.modal-body').innerHTML = html
    })

    // モーダルが閉じられたら削除
    modalElement.addEventListener('hidden.bs.modal', () => {
      modalElement.remove()
    })
  }
}
